import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { OfferCardWithFeatures } from '@/entities/offer'
import { OfferCardsSkeleton } from '@/entities/offer/ui/OfferCardsSkeleton'
import { ShowedContentType } from '@/shared/types'

import { useOtherRooms } from '../../model/useOtherRooms'
import { TabSection } from '../../types'

export const OtherRoomsSection = ({ offer, callback }: TabSection) => {
	const { data, isPending, isFetching, isError } = useOtherRooms(offer)
	const { t } = useTranslation('room-page', { keyPrefix: 'otherRooms' })

	const showedContentType = useMemo(() => {
		if (isFetching || isPending) return ShowedContentType.skeleton

		if (data && isError) return ShowedContentType.error
		if (isError) return ShowedContentType.error
		if (data) return ShowedContentType.content

		return ShowedContentType.error
	}, [isFetching, isPending, data, isError])

	return (
		<section className="flex items-center justify-center gap-4">
			{showedContentType === ShowedContentType.error && (
				<h2 className="ts-headline-2">{t('notFound')}</h2>
			)}

			{showedContentType !== ShowedContentType.error && (
				<div className="relative z-0 mt-4 flex w-full min-w-full flex-1 flex-wrap items-stretch justify-center gap-4 bg-white pt-0.5 pb-4 sm:mt-0 sm:justify-evenly sm:pb-0 sm:max-xl:overflow-x-scroll xl:min-w-[38rem] xl:justify-between">
					<h2 className="sr-only">{t('offers')}</h2>

					{showedContentType === ShowedContentType.skeleton && (
						<OfferCardsSkeleton count={12} />
					)}

					{showedContentType === ShowedContentType.content && data && (
						<div className="flex min-w-full flex-wrap justify-center gap-4 pt-0.5 md:justify-between xl:min-w-[38rem]">
							{data.pages.map(page => {
								return page.offers.map(offer => {
									return (
										<div
											className="w-full max-w-[18.5rem] max-sm:max-w-[21rem]"
											key={offer.IDs.nonPersistingOfferId}
										>
											<OfferCardWithFeatures
												offer={offer}
												onNavigation={callback}
												forceFeaturesIsClickable={false}
												showSelectedOption
											/>
										</div>
									)
								})
							})}
						</div>
					)}
				</div>
			)}
		</section>
	)
}
